<template>
  <div
    v-image-modal
    v-observe-visibility="{
      callback: visibilityChanged,
      once: true,
      throttle: 100,
      intersection: {
        rootMargin: '200px 0px'
      }
    }"
  >
    <div v-if="table_list_loaded && (isVisible || !lazy_load)" class="no-page-break-inside mb-4">
      <div v-if="!modal_table && table_mode === 'newtab'" class="line-height-lg">
        <div
          v-if="show_title == true"
          :class="`data-table-${data_table_id}`"
          class="business-case-link cursor mb-2 fw-500"
          :style="main_color"
          @click="redirectToSamplePage"
        >
          <b-icon icon="table" class="mr-1" />
          {{ lang != 'ENG' ? data_table_info?.title : data_table_info?.title_e }}
        </div>

        <div class="mb-4 hide">
          <b-table-simple
            responsive
            :class="table_style === 'basic_table' ? 'edi-table' : 'no-line-table'"
            :borderless="!show_table_line"
            :outlined="show_table_outline"
          >
            <b-thead :class="{ invisible: hideHeader(data_table_id) }">
              <b-tr>
                <b-td
                  v-for="(prop, idx) in displayHeader"
                  :key="idx"
                  :style="getHeaderStyle(data_table_id, -1, idx)"
                >
                  <span v-html="formattedText(prop)"></span>
                </b-td>
              </b-tr>
            </b-thead>

            <b-tbody>
              <b-tr v-for="(cells, rowIndex) in mergedRows" :key="`row-${rowIndex}`">
                <b-td
                  v-for="cell in cells"
                  :key="`cell-${cell.rowIndex}-${cell.colIndex}`"
                  :rowspan="cell.rowspan"
                  :colspan="cell.colspan"
                  :style="getBodyCellStyle(rowIndex, colIndex)"
                >
                  <span v-html="formattedText(cell.value)"></span>
                </b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
          <p
            v-if="data_table_info?.footnote"
            class="fw-400 f-90 mb-0 text-muted w-100"
            style="white-space: pre-line"
          >
            {{ lang !== 'ENG' ? data_table_info.footnote : data_table_info.footnote_e }}
          </p>
        </div>
      </div>

      <div v-else-if="modal_table === false && table_mode === 'accordion'" class="line-height-lg">
        <div
          v-if="show_title == true"
          :class="`data-table-${data_table_id}`"
          class="business-case-link cursor mt-2 mb-2 fw-500"
          :style="main_color"
          @click="hideDataTableFromAccordion"
        >
          <b-icon icon="table" class="mr-1" :style="`color: ${selected_report?.main_color};`" />
          {{ lang != 'ENG' ? data_table_info?.title : data_table_info?.title_e }}

          <div class="float-right mb-2">
            <span
              v-if="data_table_info?.unit"
              class="f-85 gray-600 mb-0"
              style="white-space: pre-line"
            >
              {{ lang != 'ENG' ? data_table_info?.unit : data_table_info?.unit_e }}
            </span>
            <b-button
              v-if="!print_mode"
              variant="outline-dark"
              size="sm"
              :title="lang != 'ENG' ? '엑셀 다운로드' : 'Download Excel'"
              :aria-label="lang != 'ENG' ? '엑셀 다운로드' : 'Download Excel'"
              @click="download_table_excel"
            >
              <b-icon icon="file-earmark-spreadsheet" scale="1" shift-v="0" aria-hidden="true" />
              <span class="d-none d-md-inline">EXCEL</span>
            </b-button>
          </div>
        </div>

        <div class="mb-4">
          <b-table-simple
            responsive
            :class="table_style === 'basic_table' ? 'edi-table' : 'no-line-table'"
            :borderless="!show_table_line"
            :outlined="show_table_outline"
          >
            <b-thead :class="{ invisible: hideHeader(data_table_id) }">
              <b-tr>
                <b-td
                  v-for="(prop, idx) in displayHeader"
                  :key="idx"
                  :style="getHeaderStyle(data_table_id, -1, idx)"
                >
                  <span v-html="formattedText(prop)"></span>
                </b-td>
              </b-tr>
            </b-thead>

            <b-tbody>
              <b-tr v-for="(cells, rowIndex) in mergedRows" :key="`row-${rowIndex}`">
                <b-td
                  v-for="(cell, colIndex) in cells"
                  :key="`cell-${cell.rowIndex}-${cell.colIndex}`"
                  :rowspan="cell.rowspan"
                  :colspan="cell.colspan"
                  :style="getBodyCellStyle(rowIndex, colIndex)"
                >
                  <span v-html="formattedText(cell.value)"></span>
                </b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
          <p
            v-if="data_table_info?.footnote"
            class="fw-400 f-90 mb-0 text-muted w-100"
            style="white-space: pre-line"
          >
            {{ lang !== 'ENG' ? data_table_info.footnote : data_table_info.footnote_e }}
          </p>
        </div>
      </div>

      <div v-else-if="modal_table === true || table_mode === 'popup'">
        <a class="mb-2 mr-3 cursor" :style="main_color" @click="showDataTable()">
          <b-icon icon="table" class="mr-1" :style="`color: ${selected_report?.main_color};`" />
          {{ lang != 'ENG' ? data_table_info?.title : data_table_info?.title_e }}
        </a>

        <div
          v-show="opened_modal_id.includes('data_table_' + data_table_id)"
          class="hj-modal-example bg-white rounded shadow"
        >
          <div
            class="hj-modal-header fw-500 d-flex align-items-center justify-content-between px-3"
          >
            <div>
              <b-badge variant="border-light" class="mr-2">Data Table.</b-badge>
              {{ lang != 'ENG' ? data_table_info?.title : data_table_info?.title_e }}
            </div>
            <div style="min-width: 74px" align="right">
              <b-button
                variant="icon-light"
                class="py-0 px-2 mr-1"
                @click="low_height = !low_height"
              >
                <fa :icon="low_height == true ? 'expand' : 'compress-alt'" /> </b-button
              ><b-button variant="icon-light" class="py-0 px-2" @click="closeDataTable()">
                <fa icon="times" />
              </b-button>
            </div>
          </div>

          <div
            class="hj-modal-body p-3"
            :style="low_height == true ? 'height:calc(50vh - 50px)!important' : ''"
          >
            <b-table-simple
              responsive
              :class="table_style === 'basic_table' ? 'edi-table' : 'no-line-table'"
              :borderless="!show_table_line"
              :outlined="show_table_outline"
            >
              <b-thead :class="{ invisible: hideHeader(data_table_id) }">
                <b-tr>
                  <b-td
                    v-for="(prop, idx) in displayHeader"
                    :key="idx"
                    :style="getHeaderStyle(data_table_id, -1, idx)"
                  >
                    <span v-html="formattedText(prop)"></span>
                  </b-td>
                </b-tr>
              </b-thead>

              <b-tbody>
                <b-tr v-for="(cells, rowIndex) in mergedRows" :key="`row-${rowIndex}`">
                  <b-td
                    v-for="(cell, colIndex) in cells"
                    :key="`cell-${cell.rowIndex}-${cell.colIndex}`"
                    :rowspan="cell.rowspan"
                    :colspan="cell.colspan"
                    :style="getBodyCellStyle(rowIndex, colIndex)"
                  >
                    <span v-html="formattedText(cell.value)"></span>
                  </b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
            <p
              v-if="data_table_info?.footnote"
              class="fw-400 f-90 mb-0 text-muted w-100"
              style="white-space: pre-line"
            >
              {{ lang !== 'ENG' ? data_table_info.footnote : data_table_info.footnote_e }}
            </p>
          </div>
        </div>
      </div>

      <div v-else>
        <b-skeleton class="mb-4" />
      </div>
    </div>

    <div v-else-if="lazy_load && !isVisible" class="data-table-placeholder mb-4">
      <b-icon icon="table" class="mr-1" :style="`color: ${selected_report?.main_color};`" />
      <span class="text-muted">{{ lang != 'ENG' ? 'Loading table...' : 'Loading table...' }}</span>
    </div>

    <div v-else>
      <b-skeleton class="mb-4" />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import { gri_mixin } from '@/components/writing_report/write_report_subs/gri_mixin';
import { formattedText } from '@/components/util';

export default {
  name: 'DataTable',
  components: {},
  mixins: [gri_mixin],
  props: {
    data_table_data: { type: Object, default: () => null },
    data_table_id: { type: [String, Number], default: '' },
    modal_table: { type: Boolean, default: true },
    lazy_load: { type: Boolean, default: true },
    data_table_obj: {
      type: Object,
      default() {
        return {};
      }
    },
    show_title: { type: Boolean, default: true },
    table_mode: { type: String, default: 'accordion' },
    print_mode: { type: Boolean, default: () => false },
    table_style: { type: String, default: 'basic_table' },
    show_table_line: { type: Boolean, default: true },
    show_table_outline: { type: Boolean, default: false },
    table_header_color: { type: String, default: '#dedede' },
    table_header_font_color: { type: String, default: '#fffff' }
  },
  data() {
    return {
      low_height: false,
      isVisible: false
    };
  },
  computed: {
    ...mapState(['backend_host']),
    ...mapState('report_list', ['report_code', 'lang', 'view_mode']),
    ...mapGetters('report_contents', [
      'data_table_header_style',
      'data_table_cell_style',
      'table_vertical_padding',
      'table_horizontal_padding'
    ]),
    ...mapGetters('report_list', ['selected_report']),
    ...mapState('gri2021_modal_open_status', ['opened_modal_id']), //open된 bc 확인
    ...mapState('data_tables', ['table_list', 'table_list_loaded']),
    data_table_info() {
      const idx = this.table_list.findIndex(item => {
        return item.table_id == this.data_table_id;
      });
      if (idx > -1) {
        return this.table_list[idx];
      } else {
        return {};
      }
    },
    main_color() {
      return `color:` + this.selected_report?.main_color;
    },
    main_color_sub() {
      return (
        `color:` +
        this.selected_report?.main_color +
        `; border-top: 1px solid ` +
        this.selected_report?.main_color +
        `!important` +
        `; border-bottom: 1px solid ` +
        this.selected_report?.main_color
      );
    },

    displayHeader() {
      const info = this.data_table_info;

      return this.lang !== 'ENG' ? info?.header : info?.header_e || info?.header;
    },
    displayContents() {
      const info = this.data_table_info;
      if (!info) return [];
      return this.lang !== 'ENG'
        ? info.contents || []
        : info.contents_e && info.contents_e.length > 0
        ? info.contents_e
        : info.contents || [];
    },

    mergedRows() {
      return this.visibleCellsFromDataInfo();
    }
  },
  methods: {
    ...mapMutations('gri2021_modal_open_status', [
      'add_opened_data_table_id',
      'delete_opened_data_table_id'
    ]),
    formattedText,
    visibilityChanged(isVisible) {
      if (isVisible && !this.isVisible) {
        this.isVisible = true;
      }
    },
    showDataTable() {
      this.add_opened_data_table_id(this.data_table_id);
    },
    closeDataTable() {
      this.delete_opened_data_table_id(this.data_table_id);
    },
    hideDataTableFromAccordion(event) {
      const nextEl = event.target.nextElementSibling;
      if (nextEl) {
        nextEl.classList.toggle('hide');
      }
    },
    redirectToSamplePage() {
      if (this.lang !== 'ENG') {
        const newTab = window.open('', '_blank');
        if (newTab) {
          newTab.location.href =
            this.$store.state.frontend_host + `/form_gallery?type=dtable&id=${this.data_table_id}`;
        }
      } else {
        const newTab = window.open('', '_blank');
        if (newTab) {
          newTab.location.href =
            this.$store.state.frontend_host +
            `/form_gallery?type=dtable&id=${this.data_table_id}&lang=ENG`;
        }
      }
    },

    async download_table_excel() {
      const link = document.createElement('a');
      link.href = `${this.$store.state.backend_host}/report/download_table_excel?ti=${this.data_table_id}&lang=${this.lang}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    },

    getHeaderCellStyle(col_index) {
      const key = `-1, ${col_index}`;

      const opt = {
        ...this.data_table_info?.options?.[key]
      };

      if (opt) {
        return {
          border: `${opt.borderWidth}px ${opt.borderType} ${opt.borderColor} !important`,
          'border-top': opt.borderSide?.[0] ? '1' : '0',
          'border-bottom': opt.borderSide?.[1] ? '1' : '0',
          'border-left': opt.borderSide?.[2] ? '1' : '0',
          'border-right': opt.borderSide?.[3] ? '1' : '0',
          padding: `${this.table_vertical_padding}rem ${this.table_horizontal_padding}rem`,
          'background-color': opt.backgroundColor
        };
      }
      if (this.table_style === 'custom_table') {
        return {
          'background-color': this.table_header_color,
          color: this.table_header_font_color
        };
      }
      return {};
    },
    // 바디 셀 전용 스타일 메소드
    getBodyCellStyle(row_index, header_index) {
      const key = `${row_index}, ${header_index}`;
      const opt = {
        ...this.data_table_cell_style,
        ...this.data_table_info?.options?.[key]
      };
      if (!opt) return {};
      return {
        border: `${opt.borderWidth}px ${opt.borderType} ${opt.borderColor} !important`,
        'border-top': opt.borderSide?.[0] ? '1' : '0',
        'border-bottom': opt.borderSide?.[1] ? '1' : '0',
        'border-left': opt.borderSide?.[2] ? '1' : '0',
        'border-right': opt.borderSide?.[3] ? '1' : '0',
        padding: `${this.table_vertical_padding}rem ${this.table_horizontal_padding}rem`,
        'background-color': `${opt.backgroundColor} !important`,
        verticalAlign: opt?.verticalAlign || 'top',
        textAlign: opt?.textAlign || 'start'
      };
    },

    // data_table_info로부터 rowspan/colspan, hidden 처리 후 2차원 배열 반환
    visibleCellsFromDataInfo() {
      const rows = this.displayContents;
      if (!rows) return [];

      // 1) 셀 객체 생성
      const cells = rows.map((row, r) =>
        this.displayHeader.map((prop, c) => {
          const key = `${r}, ${c}`;
          const meta = this.data_table_info.options?.[key] || {};
          const opt = {
            ...this.data_table_cell_style,
            ...meta
          };
          return {
            rowIndex: r,
            colIndex: c,
            value: row[prop],
            colspan: opt.colspan || 1,
            rowspan: opt.rowspan || 1,
            hidden: opt.hidden || false,
            style: this.getBodyCellStyle(r, c)
          };
        })
      );

      // 2) 1열 특수 병합: 연속 같은 값이면 rowspan 누적
      for (let r = 0; r < cells.length; r++) {
        const cell = cells[r][0];
        if (cell.hidden) continue;
        let span = cell.rowspan;
        for (let r2 = r + 1; r2 < cells.length; r2++) {
          const next = cells[r2][0];
          if (next.value === cell.value) {
            next.hidden = true;
            span += next.rowspan;
          } else break;
        }
        cell.rowspan = span;
      }

      // 3) hidden 제거하고 행별로 return
      return cells.map(row => row.filter(cell => !cell.hidden));
    }
  },
  mounted() {
    // If lazy_load is disabled, show immediately
    if (!this.lazy_load) {
      this.isVisible = true;
    }
  },
  created() {}
};
</script>

<style scoped>
::v-deep td img,
::v-deep th img {
  cursor: pointer;
}

.data-table-placeholder {
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
  margin: 5px 0;
  display: flex;
  align-items: center;
}
</style>
